import React, { useState, useEffect, useContext } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import Url from '../Url';
import './Bulkwhatsapp.css'; // Ensure this is imported
import { LoginContext } from '../Login/LoginContext';

function ExcelTable() {
    const { loginResult } = useContext(LoginContext);
    const userId = loginResult?.user_id;

    const [mobileNumbers, setMobileNumbers] = useState([]);
    const [invalidMobileNumbers, setInvalidMobileNumbers] = useState([]); // State for invalid numbers (now includes duplicates)
    const [showInvalidNumbersPopup, setShowInvalidNumbersPopup] = useState(false); // New state for popup visibility
    const [activeTab, setActiveTab] = useState('text');
    const [messageText, setMessageText] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [sending, setSending] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');

    const [qrImage, setQrImage] = useState(null);
    const [waReady, setWaReady] = useState(false); // State to track WhatsApp connection status
    const [qrMessage, setQrMessage] = useState('');
    const [polling, setPolling] = useState(false);
    const [hasOpenedQrSection, setHasOpenedQrSection] = useState(false);
    const [qrLoading, setQrLoading] = useState(false);

    // State to control the visibility of the WhatsApp QR section (will act as offcanvas trigger)
    const [showQrSection, setShowQrSection] = useState(false);

    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
    const acceptTypes = allowedExtensions.join(',');

    useEffect(() => {
        const handleKeyDown = async (e) => {
            if (e.key === 'F4') {
                e.preventDefault();
                if (showQrSection) {
                    setShowQrSection(false);
                    setPolling(false);
                    setQrLoading(false);
                } else {
                    setShowQrSection(true);
                    setHasOpenedQrSection(true);
                    if (!userId) {
                        setQrMessage('Please log in to start WhatsApp session.');
                        setQrLoading(false);
                        return;
                    }
                    setQrLoading(true);
                    setQrMessage('🟡 Starting WhatsApp...');
                    setPolling(true);
                    try {
                        await fetch(`${Url.Url}/start-whatsapp/${userId}`, { method: 'POST' });
                    } catch (err) {
                        console.error('❌ Failed to start WhatsApp:', err);
                        setQrMessage('Failed to start WhatsApp session.');
                        setPolling(false);
                    } finally {
                        setQrLoading(false);
                    }
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [userId, showQrSection]);

    const fetchQr = async () => {
        if (!userId) {
            setQrMessage('Please log in to start WhatsApp session.');
            setQrLoading(false);
            setPolling(false);
            return;
        }

        setQrLoading(true);
        try {
            const res = await fetch(`${Url.Url}/whatsapp-qr/${userId}`);
            const data = await res.json();

            if (res.status === 404) {
                setQrImage(null);
                setWaReady(false); // WhatsApp not ready
                setQrMessage('❌ No session found. Press F4 to start WhatsApp.');
                setPolling(false);
                return;
            }

            if (data.ready) {
                setQrImage(null);
                setWaReady(true); // WhatsApp is ready
                setQrMessage('✅ WhatsApp is connected.');
                setPolling(false);
            } else if (data.qr) {
                setQrImage(data.qr);
                setWaReady(false); // WhatsApp not ready (showing QR)
                setQrMessage('📲 Scan this QR code using WhatsApp on your phone.');
            } else if (data.message) {
                setQrImage(null);
                setWaReady(false); // WhatsApp not ready (showing message)
                setQrMessage(`ℹ️ ${data.message}`);
            }
        } catch (error) {
            console.error('❌ Failed to fetch QR:', error);
            setQrImage(null);
            setWaReady(false); // WhatsApp not ready (error)
            setQrMessage('🚫 Server error. Please check your internet or backend.');
            setPolling(false);
        } finally {
            setQrLoading(false);
        }
    };

    useEffect(() => {
        if (!polling || !userId || !showQrSection) {
            return;
        }

        fetchQr();

        const interval = setInterval(() => {
            if (polling && showQrSection) {
                fetchQr();
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [polling, userId, showQrSection]);

    const handleResetWhatsApp = async () => {
        if (!userId) return;
        setQrLoading(true);
        setQrMessage('♻️ Resetting session...');
        try {
            await fetch(`${Url.Url}/reset-one/${userId}`, { method: 'POST' });
            setQrImage(null);
            setWaReady(false);
            setQrMessage('♻️ Session reset. Starting new session...');
            setPolling(true);
            await fetch(`${Url.Url}/start-whatsapp/${userId}`, { method: 'POST' });
            fetchQr(); // Fetch new QR/status immediately after starting
        } catch (error) {
            console.error('❌ Reset failed:', error);
            setQrMessage('Failed to reset WhatsApp session.');
            setPolling(false);
        } finally {
            setQrLoading(false);
        }
    };

    // Validation function for mobile numbers
    const isValidMobileNumber = (number) => {
        // Updated regex to ensure it's a 10-digit number starting with 6, 7, 8, or 9
        const mobileNumberRegex = /^[6-9]\d{9}$/;
        return mobileNumberRegex.test(String(number));
    };

    const handleImport = (e) => {
        const file = e.target.files;
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const workbook = XLSX.read(bstr, { type: 'binary' });
            const wsname = workbook.SheetNames;
            const worksheet = workbook.Sheets[wsname];
            // Use header: 1 to get an array of arrays (rows)
            // Use defval: '' to ensure undefined cells become empty strings
            const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }); // Added defval

            if (data.length === 0) {
                alert('The imported Excel file is empty!');
                setMobileNumbers([]);
                setInvalidMobileNumbers([]);
                setShowInvalidNumbersPopup(false);
                return;
            }

            const headers = data;
            const mobileIndex = headers.findIndex(
                (header) => header && (String(header).toLowerCase().includes('mobile') || String(header).toLowerCase().includes('phone'))
            );

            if (mobileIndex === -1) {
                alert('Mobile number column not found! Please ensure your Excel has a "Mobile" or "Phone" header.');
                setMobileNumbers([]);
                setInvalidMobileNumbers([]);
                setShowInvalidNumbersPopup(false);
                return;
            }

            const uniqueValidNumbers = [];
            const invalidAndDuplicateNumbers = []; // Combined array for popup
            const seenNumbers = new Set(); // To track seen valid numbers for deduplication

            // Start from the second row (index 1) to skip headers
            data.slice(1).forEach((row, rowIndex) => {
                // Ensure the row has enough columns to access mobileIndex
                // Adding 2 to rowIndex to get actual Excel row number (0-indexed array + 1 for header row + 1 for current row)
                if (row.length <= mobileIndex) {
                    // If you truly don't want to show these rows at all,
                    // you would *remove* or *comment out* the following push.
                    // For now, keeping it as per original but noting it.
                    invalidAndDuplicateNumbers.push({ number: `Row ${rowIndex + 2} (Mobile column not found)`, reason: 'Column Missing' });
                    return; // Skip this row as it doesn't have the expected column
                }

                let rawNumber = row[mobileIndex];
                let number = String(rawNumber).trim(); // Ensure it's a string and trim whitespace

                if (!number) { // Catches empty strings after trimming, or if rawNumber was null/undefined
                    // IF YOU DON'T WANT TO SHOW EMPTY NUMBERS IN THE POPUP AT ALL,
                    // REMOVE THE FOLLOWING 4 LINES:
                    /*
                    invalidAndDuplicateNumbers.push({
                        number: rawNumber === '' ? `Row ${rowIndex + 2} (Empty Cell)` : `Row ${rowIndex + 2} (Undefined/Null)`,
                        reason: 'Empty Number'
                    });
                    */
                    return; // Skip empty numbers entirely from consideration for the popup
                }

                if (isValidMobileNumber(number)) {
                    if (!seenNumbers.has(number)) {
                        uniqueValidNumbers.push({ number: number, status: '' });
                        seenNumbers.add(number);
                    } else {
                        // This is a duplicate valid number
                        invalidAndDuplicateNumbers.push({ number: number, reason: 'Duplicate' });
                    }
                } else {
                    // This is an invalid format number
                    invalidAndDuplicateNumbers.push({ number: number, reason: 'Invalid Format' });
                }
            });

            setMobileNumbers(uniqueValidNumbers); // Set only unique, valid numbers
            setInvalidMobileNumbers(invalidAndDuplicateNumbers); // Now includes duplicates and invalid formats

            if (invalidAndDuplicateNumbers.length > 0) {
                setStatusMessage(`⚠️ Imported ${uniqueValidNumbers.length} valid numbers. ${invalidAndDuplicateNumbers.length} invalid or duplicate numbers found.`);
                setShowInvalidNumbersPopup(true);
            } else {
                setStatusMessage(`✅ Successfully imported ${uniqueValidNumbers.length} mobile numbers.`);
                setShowInvalidNumbersPopup(false);
            }
        };

        reader.onerror = () => {
            setStatusMessage('❌ Error reading file.');
            setMobileNumbers([]);
            setInvalidMobileNumbers([]);
            setShowInvalidNumbersPopup(false);
        };

        reader.readAsBinaryString(file);
    };

    const handleRemoveAllInvalidNumbers = () => {
        console.log('Remove All Wrong Numbers button clicked!'); // Added for debugging
        setInvalidMobileNumbers([]);
        setShowInvalidNumbersPopup(false);
        setStatusMessage('Removed all invalid numbers from the list.');
    };

    const handleCloseInvalidNumbersPopup = () => {
        setShowInvalidNumbersPopup(false);
        setStatusMessage('Invalid numbers popup closed.');
    };

    const handleExport = () => {
        if (mobileNumbers.length === 0) {
            alert('No mobile numbers to export!');
            return;
        }
        const data = [['Mobile Number', 'Status'], ...mobileNumbers.map((item) => [item.number, item.status])];
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'MobileNumbers');
        XLSX.writeFile(wb, 'mobile_numbers_with_status.xlsx');
    };

    const handleSendTextMessage = async () => {
        if (!userId) {
            setStatusMessage('❌ User not logged in.');
            return;
        }
        if (!waReady) {
            setStatusMessage('❌ WhatsApp is not connected. Please connect it first.');
            return;
        }
        if (mobileNumbers.length === 0) {
            setStatusMessage('❌ No mobile numbers imported to send messages to.');
            return;
        }
        if (!messageText.trim()) {
            setStatusMessage('❌ Please type a message to send.');
            return;
        }

        setSending(true);
        setStatusMessage('🟡 Sending text messages...');
        let successfulSends = 0;
        let failedSends = 0;
        let updatedNumbers = [...mobileNumbers];

        for (let i = 0; i < updatedNumbers.length; i++) {
            const number = updatedNumbers[i].number;
            try {
                const response = await axios.post(`${Url.Url}/send-bulk-whatsapp/${userId}`, {
                    numbers: [number],
                    message: messageText
                });
                const res = response.data;
                if (res.success && res.success.length > 0) {
                    updatedNumbers[i].status = 'Sent Successfully';
                    successfulSends++;
                } else if (res.failed && res.failed.length > 0) {
                    updatedNumbers[i].status = `Failed: ${res.failed.error}`;
                    failedSends++;
                } else {
                    updatedNumbers[i].status = 'Failed: Unknown response';
                    failedSends++;
                }
            } catch (error) {
                console.error(`Error sending to ${number}:`, error);
                updatedNumbers[i].status = `Failed: Network/API Error`;
                failedSends++;
            }
            setMobileNumbers([...updatedNumbers]);
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        setSending(false);
        if (failedSends === 0) {
            setStatusMessage(`✅ Successfully sent messages to all ${successfulSends} numbers.`);
        } else if (successfulSends > 0) {
            setStatusMessage(`⚠️ Sent messages to ${successfulSends} numbers. Failed for ${failedSends} numbers.`);
        } else {
            setStatusMessage(`❌ Failed to send messages to all ${failedSends} numbers.`);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files;
        if (!file) {
            setSelectedFile(null);
            return;
        }
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (allowedExtensions.includes(fileExtension)) {
            setSelectedFile(file);
            setStatusMessage(`Selected file: ${file.name}`);
        } else {
            alert("❌ Invalid file type. Please select a PDF or image file (PDF, JPG, JPEG, PNG).");
            setSelectedFile(null);
            setStatusMessage('❌ Invalid file type.');
        }
    };

    const handleSendFile = async () => {
        if (!userId) {
            setStatusMessage('❌ User not logged in.');
            return;
        }
        if (!waReady) {
            setStatusMessage('❌ WhatsApp is not connected. Please connect it first.');
            return;
        }
        if (mobileNumbers.length === 0) {
            setStatusMessage('❌ No mobile numbers imported to send files to.');
            return;
        }
        if (!selectedFile) {
            setStatusMessage('❌ Please select a file to send.');
            return;
        }
        setSending(true);
        setStatusMessage('🟡 Sending file(s)...');
        let successfulSends = 0;
        let failedSends = 0;
        let updatedNumbers = [...mobileNumbers];

        for (let i = 0; i < updatedNumbers.length; i++) {
            const number = updatedNumbers[i].number;
            const formData = new FormData();
            formData.append("file", selectedFile);
            formData.append("numbers", JSON.stringify([number]));

            try {
                const response = await axios.post(
                    `${Url.Url}/send-bulk-media/${userId}`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );

                const res = response.data;
                if (res.success && res.success.length > 0) {
                    updatedNumbers[i].status = 'Sent Successfully';
                    successfulSends++;
                } else if (res.failed && res.failed.length > 0) {
                    updatedNumbers[i].status = `Failed: ${res.failed.error}`;
                    failedSends++;
                } else {
                    updatedNumbers[i].status = 'Failed: Unknown response';
                    failedSends++;
                }
            } catch (error) {
                console.error(`Error sending file to ${number}:`, error);
                updatedNumbers[i].status = `Failed: Network/API Error`;
                failedSends++;
            }
            setMobileNumbers([...updatedNumbers]);
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        setSending(false);
        if (failedSends === 0) {
            setStatusMessage(`✅ Successfully sent files to all ${successfulSends} numbers.`);
        } else if (successfulSends > 0) {
            setStatusMessage(`⚠️ Sent files to ${successfulSends} numbers. Failed for ${failedSends} numbers.`);
        } else {
            setStatusMessage(`❌ Failed to send files to all ${failedSends} numbers.`);
        }
    };

    const getStatusMessageClass = () => {
        if (statusMessage.startsWith('✅')) return 'successbulksender';
        if (statusMessage.startsWith('❌')) return 'errorbulksender';
        if (statusMessage.startsWith('⚠️') || statusMessage.startsWith('🟡')) return 'warningbulksender';
        return '';
    };

    return (
        <div className="bulk-whatsapp-containerbulksender">
            <div className="left-columnbulksender">
                <input type="file" accept=".xlsx, .xls" onChange={handleImport} />

                {mobileNumbers.length > 0 && (
                    <div className="excel-table-containerbulksender">
                        <table className="excel-tablebulksender">
                            <thead>
                                <tr>
                                    <th>Mobile Number</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mobileNumbers.map((item, i) => (
                                    <tr key={i}>
                                        <td>{item.number}</td>
                                        <td className={`status-cellbulksender ${item.status.includes('Successfully') ? 'status-successbulksender' : item.status.includes('Failed') ? 'status-failedbulksender' : ''}`}>
                                            {item.status}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {mobileNumbers.length === 0 && <p style={{ marginTop: '20px', color: '#666' }}>Import an Excel file to see numbers here.</p>}
                <button className="bulk-whatsapp-buttonbulksender" onClick={handleExport} disabled={mobileNumbers.length === 0}>Export Mobile Numbers</button>
            </div>

            <div className="right-columnbulksender">

                {/* Initial warning/button to open offcanvas */}
                {!showQrSection && !hasOpenedQrSection && (
                    <div className="whatsapp-initial-warningbulksender">
                        <p>Press <span className="key-hintbulksender">F4</span> to connect to WhatsApp and start messaging.</p>
                    </div>
                )}
                {waReady && (
                    <div>
                        {/* Messaging controls - disabled if not waReady */}
                        <div className="tab-buttonsbuksleander" >
                            <button
                                className={`tab-buttonbulksender ${activeTab === 'text' ? 'activebulksender' : ''}`}
                                onClick={() => setActiveTab('text')}
                                disabled={sending || !waReady}
                            >
                                Send Text Message
                            </button>
                            <button
                                className={`tab-buttonbulksender ${activeTab === 'file' ? 'activebulksender' : ''}`}
                                onClick={() => setActiveTab('file')}
                                disabled={sending || !waReady}
                            >
                                Send File
                            </button>
                        </div>
                        <div className="tab-contentbulksender">
                            {activeTab === 'text' && (
                                <div>
                                    <h3>Type your message:</h3>
                                    <textarea
                                        className="message-textareabulksender"
                                        placeholder="Enter your WhatsApp message here..."
                                        value={messageText}
                                        onChange={(e) => setMessageText(e.target.value)}
                                        disabled={sending || !waReady}
                                    ></textarea>
                                    <button
                                        className="bulk-whatsapp-buttonbulksender"
                                        onClick={handleSendTextMessage}
                                        disabled={sending || mobileNumbers.length === 0 || !messageText.trim() || !waReady}
                                    >
                                        {sending ? 'Sending...' : `Send to ${mobileNumbers.length} Numbers`}
                                    </button>
                                </div>
                            )}

                            {activeTab === 'file' && (
                                <div>
                                    <h3>Select a file to send:</h3>
                                    <div className="file-input-wrapperbulksender">
                                        <label className="file-upload-labelbulksender">
                                            Choose File
                                            <input
                                                type="file"
                                                accept={acceptTypes}
                                                onChange={handleFileChange}
                                                disabled={sending || !waReady}
                                            />
                                        </label>
                                        {selectedFile && (
                                            <span className="file-name-displaybulksender">{selectedFile.name}</span>
                                        )}
                                    </div>
                                    <button
                                        className="bulk-whatsapp-buttonbulksender"
                                        onClick={handleSendFile}
                                        disabled={sending || mobileNumbers.length === 0 || !selectedFile || !waReady}
                                    >
                                        {sending ? 'Sending File...' : `Send File to ${mobileNumbers.length} Numbers`}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div className={`whatsapp-qr-sectionbulksender ${showQrSection ? 'offcanvas-openbulksender' : 'offcanvas-closedbulksender'}`}>
                    {showQrSection && (
                        <button className="close-offcanvas-btnbulksender" onClick={() => {
                            setShowQrSection(false);
                            setPolling(false);
                            setQrLoading(false);
                        }}>
                            &times;
                        </button>
                    )}


                    {waReady ? (
                        <>
                            <div className="whatsapp-connected-statusbulksender">✅ WhatsApp Connected</div>
                            <button
                                onClick={handleResetWhatsApp}
                                className="bulk-whatsapp-buttonbulksender reset-btnbulksender"
                                disabled={qrLoading || sending}
                            >
                                Reset WhatsApp
                            </button>
                        </>
                    ) : (
                        <>
                            {qrLoading ? (
                                <div className="whatsapp-messagebulksender">Loading QR...</div>
                            ) : (
                                qrImage ? (
                                    <img src={qrImage} alt="WhatsApp QR" />
                                ) : (
                                    <div className="whatsapp-messagebulksender">
                                        {qrMessage || 'Scan QR to connect WhatsApp (Press F4 to open offcanvas and connect)'}
                                    </div>
                                )
                            )}
                            {qrMessage && <div className="whatsapp-messagebulksender">{qrMessage}</div>}
                            <button
                                onClick={fetchQr}
                                className="bulk-whatsapp-buttonbulksender"
                                disabled={qrLoading || sending}
                            >
                                Get QR / Check Status
                            </button>
                        </>
                    )}

                </div>

                {statusMessage && (
                    <div className={`status-messagebulksender ${getStatusMessageClass()}`}>
                        {statusMessage}
                    </div>
                )}
            </div>

            {/* Invalid Numbers Popup */}
            {showInvalidNumbersPopup && invalidMobileNumbers.length > 0 && (
                <div className="invalid-numbers-popup-overlay">
                    <div className="invalid-numbers-popup">
                        <h4>Invalid/Duplicate Mobile Numbers Found:</h4>
                        <div className="invalid-numbers-table-container">
                            <table className="invalid-numbers-table">
                                <thead>
                                    <tr>
                                        <th>Number/Reference</th>
                                        <th>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invalidMobileNumbers.map((item, i) => (
                                        <tr key={i}>
                                            <td>{item.number}</td>
                                            <td>{item.reason}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="popup-actions">
                            <button
                                onClick={handleRemoveAllInvalidNumbers}
                                className="remove-all-invalid-btn"
                            >
                                Remove All Wrong Numbers
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ExcelTable;